import s from './UserMenu.module.sass'

import React, { FC, memo, useState } from 'react'
import { useRouter } from 'next/navigation'

import { useAppDispatch, useAppSelector } from '@app/model/store'
import { loginAC, logoutAC } from '@app/model/actions'

import { MenuLink } from '@widgets/Common/Header/ui/MenuLink'

import { Avatar } from '@shared/ui/Media/Avatar'
import { EGG } from '@shared/api/analytics'
import { middlewareAuthorized } from '@shared/lib/helpers/auth'
import { Modal } from '@shared/ui/Overlays/Modal'
import { Text } from '@shared/ui/Typography/Text'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'

const TICKETS_LINK = 'https://www.afisha.ru/w/orderList/a4910f07-c5da-47b1-b0ec-1da5418ae3f2/www.afisha.ru'

const UserMenu: FC = () => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const global = useAppSelector((state) => state.global)
  const pages = useAppSelector((state) => state.pages)
  const { isMobile } = useClientResponsive()
  const dispatch = useAppDispatch()
  const currentPageIsRestItem = global.currentPage === 'rest_item'
  const thisRestId = pages?.rest_item?.data?.id
  const thisRestSlugId = pages?.rest_item?.data?.url?.slug
  const isRestCardPage = currentPageIsRestItem && thisRestId
  const router = useRouter()
  const user = global.user.data

  const toggleDropdown = () => {
    setIsOpenDropdown(!isOpenDropdown)
    if (isOpenDropdown) EGG.accountMenu.button_popup_close_click()
  }

  const handleClickLogin = (ev) => {
    ev.preventDefault()
    EGG.header.button_login_click()
    loginAC()
  }

  const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (url && newWindow) newWindow.opener = null
  }

  if (!user) {
    return (
      // @ts-ignore
      <MenuLink
        button
        onClick={handleClickLogin}
        text={'Войти'}
        className={s.login}
        data-loading={global.user.loading}
      />
    )
  }

  // if (!user) return <MenuLink button handleClick={() => middlewareAuthorized()} text={'Войти'} />

  const fullName = `${user.first_name} ${user.last_name}`

  const currentRestHasOwner =
    currentPageIsRestItem &&
    // @ts-ignore
    user.owner_rests.some((restIdNum) => parseInt(isRestCardPage, 10) === parseInt(restIdNum, 10))

  const hasAccess = {
    special: user?.owner_perms?.has_special_access || false,
    description: user?.owner_perms?.has_description_access || false,
    booking: user?.owner_perms?.has_booking_access || false,
  }

  const isShowPanel = {
    marketing:
      isRestCardPage && (hasAccess.special || hasAccess.description || user.stf || user.spr || currentRestHasOwner),
    admin: hasAccess.special || user.stf || currentRestHasOwner,
  }

  const handleProfile = (ev) => {
    ev.preventDefault
    EGG.accountMenu.button_profile_click()
    router.push(`https://www.afisha.ru/personalpage/${user.afisha_id}/`)
  }

  const handleRestaurateur = (ev) => {
    ev.preventDefault
    if (thisRestSlugId) {
      EGG.accountMenu.button_restaurateur_click()
      openInNewTab(`https://www.afisha.ru/${global.currentCity.slug}/restaurant/${thisRestSlugId}/owner/`)
    }
  }

  const handleLike = () => {
    middlewareAuthorized(() => {
      EGG.header.button_favorite_click()
      router.push(`https://www.afisha.ru/personalpage/${user.afisha_id}/favorites/`)
    })
  }

  const handleRatings = (ev) => {
    ev.preventDefault
    EGG.accountMenu.button_reviews_click()
    router.push(`https://www.afisha.ru/personalpage/${user.afisha_id}/ratings/`)
  }

  const handleTickets = () => {
    openInNewTab(TICKETS_LINK)
  }

  const handleEditor = (ev) => {
    ev.preventDefault
    EGG.accountMenu.button_editor_click()
    openInNewTab(`https://www.afisha.ru/rests/admin/`)
  }

  const handleSettings = (ev) => {
    ev.preventDefault
    EGG.accountMenu.button_settings_click()
    router.push(`https://id.rambler.ru/account/profile/`)
  }

  const handleLogout = (ev) => {
    ev.preventDefault()
    EGG.accountMenu.button_exit_click()
    dispatch(logoutAC())
  }

  const handleClickProfileAnalytic = () => {
    EGG.accountMenu.button_profile_click()
  }

  const ContentDropDown = () => (
    <>
      <div className={s['dropdown-header']}>
        <Avatar
          sizes='XL L'
          src={user.photo}
          alt={`${fullName} user-avatar`}
          className={s['dropdown-avatar']}
          userName={fullName}
          onClick={handleClickProfileAnalytic}
        />

        <Text sizes='L ML' medium className={s.fullname}>
          {fullName}
        </Text>
      </div>

      <ul className={s['dropdown-main']}>
        <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleProfile}>
          <a>{'Профиль'}</a>
        </Text>

        {isShowPanel.marketing && (
          <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleRestaurateur}>
            <a>{'Личный кабинет ресторатора'}</a>
          </Text>
        )}

        <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleTickets}>
          <a>{'Мои билеты'}</a>
        </Text>

        <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleLike}>
          <a>{'Избранное'}</a>
        </Text>

        <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleRatings}>
          <a>{'Оценки и отзывы'}</a>
        </Text>

        {isShowPanel.admin && (
          <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleEditor}>
            <a>{'Редактор материалов'}</a>
          </Text>
        )}

        <Text sizes='M' tag='li' className={s['dropdown-link']} onClick={handleSettings}>
          <a>{'Настройки'}</a>
        </Text>

        <Text sizes='M' tag='li' className={`${s['dropdown-link']}`} onClick={handleLogout}>
          <a>{'Выход'}</a>
        </Text>
      </ul>
    </>
  )

  return (
    <div className={s.user}>
      <div onClick={toggleDropdown} className={s.avatar} data-loading={global.user.loading}>
        <Avatar sizes='M S' src={user.photo} userName={fullName} alt='user-avatar' />
      </div>

      {isOpenDropdown && !isMobile && (
        <div className={s.dropdown}>
          <MenuLink close onClick={toggleDropdown} dataId={'dots'} selected className={s['dropdown-close']} />
          <ContentDropDown />
        </div>
      )}

      {isOpenDropdown && isMobile && (
        // @ts-ignore
        <Modal handleClose={toggleDropdown}>
          <div className={s.modal}>
            <ContentDropDown />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default memo(UserMenu)
